import { InviteUserRole, UserRole } from 'gql/types';
import { getTranslation } from 'utils/getTranslation';
import * as yup from 'yup';

const t = getTranslation('user.ValidationErrors');

export const usernameSchema = yup
  .string()
  .strict(true)
  .trim(t('mustBeTrimmed'))
  .required(t('required', { field: t('name') }))
  .test({
    test: (value) => (value ? !(value.length < 2) : true),
    message: t('moreThan', { min: 2 }),
  })
  .max(30, t('lessThan', { max: 30 }))
  .test({
    test: (value) => (value ? new RegExp(/^[a-z ,.'-]+$/i).test(value) : true),
    message: t('onlyAlphabets'),
  }); //Alphabets & spaces;

export const userLastNameSchema = yup
  .string()
  .strict(true)
  .trim(t('mustBeTrimmed'))
  .required(t('required', { field: t('lastName') }))
  .test({
    test: (value) => (value ? !(value.length < 2) : true),
    message: t('moreThan', { min: 2 }),
  })
  .max(30, t('lessThan', { max: 30 }))
  .test({
    test: (value) => (value ? new RegExp(/^[a-z ,.'-]+$/i).test(value) : true),
    message: t('onlyAlphabets'),
  }); //Alphabets & spaces;

export const emailSchema = yup
  .string()
  .required(t('required', { field: t('email') }))
  .max(50, t('lessThan', { max: 50 }))
  .email(t('emailMustBeValid'));

const NUMBERS_REGEX = /[0-9]/;
const ENG_LETTERS_REGEX = /(?=.*[a-z])(?=.*[A-Z])/;
const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_\-+=\[\]{};:'",.<>/?\\|`~]/;
const ONLY_PRINTABLE_CHARS_REGEX = /^[^\p{C}]+$/u;

export const passwordSchema = yup
  .string()
  .required(t('required', { field: t('password') }))
  .matches(NUMBERS_REGEX, t('numberRequired')) //Must include number
  .matches(ENG_LETTERS_REGEX, t('upperLowercaseLetterRequired')) //Must include letters
  .matches(SPECIAL_CHARS_REGEX, t('specialRequired')) //Must include special symbols
  .matches(ONLY_PRINTABLE_CHARS_REGEX, t('onlyPrintableSymbolsAllowed')) //Must not include unprintable chars
  .min(8, t('moreThan', { min: 8 }))
  .max(64, t('lessThan', { max: 64 }));

// RegEx below is used for matching such phone number:
// - (123) 456-7890
export const phoneNumberSchema = yup
  .string()
  .required(t('pleaseEnterPhoneNumber'))
  .matches(
    /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/g,
    t('phoneNumberDoesNotMatch')
  );

export const inviteUserRoleSchema = yup
  .mixed<InviteUserRole>()
  .oneOf(Object.values(InviteUserRole))
  .required(t('required', { field: t('role') }));

export const userRoleSchema = yup
  .mixed<UserRole>()
  .oneOf(Object.values(UserRole))
  .required(t('required', { field: t('role') }));

export const contactPreferencesSchema = yup
  .string()
  .transform((val) => (val === null ? undefined : val))
  .max(100, t('lessThan', { max: 100 }));

export const productRegionPreferenceSchema = yup.string();

export const assignedOfficeSchema = yup.string();

export const assignedRegionIdsSchema = yup
  .array()
  .of(yup.string().required())
  .nullable()
  .optional();

export const assignedLocationIdsSchema = yup
  .array()
  .of(yup.string().required())
  .nullable()
  .optional();
